import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import {
  GetIntegrationHistoryParams,
  IntegrationsHistoryItem,
} from "features/integrations/integrations";
import { formatIntegrationDateTime } from "utils/dateUtils";
import CustomScrollbars from "../CustomScrollbars";

interface IntegrationDetailsTableProps {
  history: IntegrationsHistoryItem[];
  isFetching: boolean;
  sortBy: GetIntegrationHistoryParams["sort_by"];
  sortOrder: "asc" | "desc";
  onSortChange: (field: GetIntegrationHistoryParams["sort_by"]) => void;
  timezone?: string;
  pagination?: React.ReactNode;
}

const noWrapCellStyle = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export default function IntegrationDetailsTable({
  history,
  isFetching,
  sortBy,
  sortOrder,
  onSortChange,
  timezone,
  pagination,
}: IntegrationDetailsTableProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 260,
        overflow: "clip",
      }}
    >
      <CustomScrollbars
        sx={{ flex: 1, minWidth: 0, overflowX: "auto" }}
        trackOffsetTop={40}
      >
        <Table
          stickyHeader
          size="small"
          sx={{ minWidth: 700, borderColor: "divider" }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }} align="center">
                <TableSortLabel
                  active={sortBy === "date"}
                  direction={sortBy === "date" ? sortOrder : "asc"}
                  onClick={() => onSortChange("date")}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">
                <TableSortLabel
                  active={sortBy === "completed"}
                  direction={sortBy === "completed" ? sortOrder : "asc"}
                  onClick={() => onSortChange("completed")}
                >
                  Completed
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={2}
                  sx={{
                    py: 4,
                    borderBottom: "none",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No history found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              history.map((detail, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:last-child td, &:last-child th": {
                      borderBottom: "none",
                    },
                    opacity: isFetching ? 0.6 : 1,
                  }}
                >
                  <TableCell sx={noWrapCellStyle} align="center">
                    {formatIntegrationDateTime(detail.date, timezone)}
                  </TableCell>
                  <TableCell align="center">
                    {detail.completed ? (
                      <CheckIcon sx={{ color: "secondary.main", fontSize: 20 }} />
                    ) : (
                      <CloseIcon sx={{ color: "error.main", fontSize: 20 }} />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CustomScrollbars>
      {pagination}
    </Paper>
  );
}
